import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Users, Plus, Tag, MessageSquare, Clock, ChevronRight,
  GripVertical, X, Send, Bell, Check, Filter, Search,
  MapPin, Briefcase, Trash2, ArrowRight, Star
} from "lucide-react";

const STAGE_CONFIG = {
  lead: { label: "Lead", color: "border-gray-500", bg: "bg-gray-500/10", text: "text-gray-400" },
  contacted: { label: "Contacted", color: "border-blue-500", bg: "bg-blue-500/10", text: "text-blue-400" },
  screening: { label: "Screening", color: "border-amber-500", bg: "bg-amber-500/10", text: "text-amber-400" },
  interview: { label: "Interview", color: "border-purple-500", bg: "bg-purple-500/10", text: "text-purple-400" },
  offer: { label: "Offer", color: "border-crimson", bg: "bg-crimson/10", text: "text-crimson" },
  hired: { label: "Hired", color: "border-emerald-500", bg: "bg-emerald-500/10", text: "text-emerald-400" },
  rejected: { label: "Rejected", color: "border-red-500", bg: "bg-red-500/10", text: "text-red-400" },
};

const TAG_PRESETS = ["Strong", "Follow Up", "Culture Fit", "Top Talent", "Needs Review", "Urgent", "Referral"];

const RecruiterCRM = () => {
  const { user } = useAuth();
  const [pipeline, setPipeline] = useState({});
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    fetchPipeline();
    fetchStats();
    fetchReminders();
  }, [selectedJob]);

  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const url = selectedJob
        ? `${API}/crm/pipeline?job_id=${selectedJob}`
        : `${API}/crm/pipeline`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPipeline(data.pipeline || {});
        setJobs(data.jobs || []);
      }
    } catch {
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/crm/pipeline/stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  const fetchReminders = async () => {
    try {
      const res = await fetch(`${API}/crm/pipeline/reminders`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setReminders(data.reminders || []);
      }
    } catch {}
  };

  const moveStage = async (entryId, newStage) => {
    try {
      const res = await fetch(`${API}/crm/pipeline/${entryId}/stage`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: newStage }),
      });
      if (res.ok) {
        toast.success(`Moved to ${STAGE_CONFIG[newStage]?.label}`);
        fetchPipeline();
      }
    } catch {
      toast.error("Failed to move candidate");
    }
  };

  const addNote = async () => {
    if (!selectedEntry || !newNote.trim()) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API}/crm/pipeline/${selectedEntry.entry_id}/notes`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: newNote }),
      });
      if (res.ok) {
        const note = await res.json();
        setSelectedEntry(prev => ({
          ...prev,
          notes: [...(prev.notes || []), note]
        }));
        setNewNote("");
        toast.success("Note added");
      }
    } catch {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const toggleTag = async (entryId, currentTags, tag) => {
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    try {
      const res = await fetch(`${API}/crm/pipeline/${entryId}/tags`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tags: newTags }),
      });
      if (res.ok) {
        if (selectedEntry?.entry_id === entryId) {
          setSelectedEntry(prev => ({ ...prev, tags: newTags }));
        }
        fetchPipeline();
      }
    } catch {}
  };

  const setReminder = async (entryId, days) => {
    try {
      const res = await fetch(`${API}/crm/pipeline/${entryId}/reminder`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days }),
      });
      if (res.ok) {
        toast.success(`Reminder set for ${days} days`);
        fetchReminders();
      }
    } catch {}
  };

  const markContacted = async (entryId) => {
    try {
      const res = await fetch(`${API}/crm/pipeline/${entryId}/contacted`, {
        method: "PUT", credentials: "include",
      });
      if (res.ok) {
        toast.success("Marked as contacted");
        fetchPipeline();
      }
    } catch {}
  };

  const removeEntry = async (entryId) => {
    try {
      const res = await fetch(`${API}/crm/pipeline/${entryId}`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) {
        toast.success("Removed from pipeline");
        setShowDetail(false);
        setSelectedEntry(null);
        fetchPipeline();
        fetchStats();
      }
    } catch {}
  };

  const filteredPipeline = (entries) => {
    if (!searchQuery) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(e =>
      e.candidate_name?.toLowerCase().includes(q) ||
      e.candidate_title?.toLowerCase().includes(q) ||
      e.tags?.some(t => t.toLowerCase().includes(q))
    );
  };

  const totalCandidates = Object.values(pipeline).flat().length;

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Sidebar />
        <main className="lg:ml-64 p-6 lg:p-8 pt-20 lg:pt-8">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="recruiter-crm-page">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">
              CANDIDATE <span className="text-crimson">PIPELINE</span>
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {totalCandidates} candidate{totalCandidates !== 1 ? "s" : ""} in pipeline
              {reminders.length > 0 && (
                <span className="ml-2 text-amber-400">
                  <Bell className="w-3 h-3 inline" /> {reminders.length} reminder{reminders.length !== 1 ? "s" : ""} due
                </span>
              )}
            </p>
          </motion.div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search candidates..."
                className="bg-[#111] border border-[#222] pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:border-crimson focus:outline-none w-48"
                data-testid="crm-search"
              />
            </div>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="bg-[#111] border border-[#222] text-white text-sm px-3 py-2 focus:border-crimson outline-none"
              data-testid="crm-job-filter"
            >
              <option value="">All Jobs</option>
              {jobs.map(j => (
                <option key={j.job_id} value={j.job_id}>{j.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Stats Strip */}
        {stats && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            {Object.entries(STAGE_CONFIG).map(([stage, cfg]) => (
              <div key={stage} className={`flex items-center gap-2 px-3 py-1.5 border ${cfg.color} ${cfg.bg} text-xs whitespace-nowrap flex-shrink-0`}>
                <span className={cfg.text}>{cfg.label}</span>
                <span className="text-white font-bold">{stats.stage_counts?.[stage] || 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Kanban Pipeline Board */}
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
          {Object.entries(STAGE_CONFIG).map(([stage, cfg]) => {
            const entries = filteredPipeline(pipeline[stage] || []);
            return (
              <div key={stage} className="flex-shrink-0 w-64" data-testid={`stage-${stage}`}>
                {/* Column Header */}
                <div className={`border-t-2 ${cfg.color} bg-[#0a0a0a] border border-[#1a1a1a] border-t-0 px-3 py-2.5 mb-2`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.label}</span>
                    <span className="text-[10px] text-gray-600 bg-[#111] px-1.5 py-0.5 rounded-full">{entries.length}</span>
                  </div>
                </div>

                {/* Cards */}
                <div className="space-y-2 min-h-[100px]">
                  {entries.map((entry) => (
                    <motion.div
                      key={entry.entry_id}
                      className="bg-[#111] border border-[#222] p-3 hover:border-[#333] cursor-pointer transition-colors group"
                      onClick={() => { setSelectedEntry(entry); setShowDetail(true); }}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      data-testid={`crm-card-${entry.entry_id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h4 className="text-sm font-medium truncate">{entry.candidate_name}</h4>
                        <GripVertical className="w-3 h-3 text-gray-600 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                      </div>
                      {entry.candidate_title && (
                        <p className="text-[10px] text-gray-500 truncate mb-2">{entry.candidate_title}</p>
                      )}
                      {/* Tags */}
                      {entry.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {entry.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-crimson/10 text-crimson border border-crimson/20">
                              {tag}
                            </span>
                          ))}
                          {entry.tags.length > 3 && <span className="text-[9px] text-gray-600">+{entry.tags.length - 3}</span>}
                        </div>
                      )}
                      {/* Meta */}
                      <div className="flex items-center justify-between text-[9px] text-gray-600">
                        {entry.notes?.length > 0 && (
                          <span className="flex items-center gap-0.5">
                            <MessageSquare className="w-2.5 h-2.5" /> {entry.notes.length}
                          </span>
                        )}
                        {entry.last_contacted && (
                          <span className="flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" /> {new Date(entry.last_contacted).toLocaleDateString()}
                          </span>
                        )}
                        {entry.reminder_date && new Date(entry.reminder_date) <= new Date() && (
                          <Bell className="w-3 h-3 text-amber-400" />
                        )}
                      </div>
                      {/* Quick move */}
                      <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {Object.entries(STAGE_CONFIG).filter(([s]) => s !== stage).slice(0, 3).map(([s, c]) => (
                          <button
                            key={s}
                            onClick={(e) => { e.stopPropagation(); moveStage(entry.entry_id, s); }}
                            className={`text-[8px] px-1.5 py-0.5 border ${c.color} ${c.text} hover:${c.bg} transition-colors`}
                            title={`Move to ${c.label}`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                  {entries.length === 0 && (
                    <div className="text-center py-6 text-gray-700 text-[10px]">
                      No candidates
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {totalCandidates === 0 && (
          <motion.div className="text-center py-16 border border-[#222] bg-[#0a0a0a] mt-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-bold mb-1">Your pipeline is empty</h3>
            <p className="text-sm text-gray-500 mb-4">Add candidates from job applications or talent search to start managing your hiring pipeline.</p>
          </motion.div>
        )}
      </main>

      {/* ── Candidate Detail Slide-over ── */}
      <AnimatePresence>
        {showDetail && selectedEntry && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDetail(false)}
            />
            <motion.div
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#0a0a0a] border-l border-[#222] z-50 overflow-y-auto"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              data-testid="crm-detail-panel"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold">{selectedEntry.candidate_name}</h2>
                  <button onClick={() => setShowDetail(false)} className="text-gray-500 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Info */}
                {selectedEntry.candidate_title && (
                  <p className="text-sm text-gray-400 mb-1">{selectedEntry.candidate_title}</p>
                )}
                {selectedEntry.candidate_location && (
                  <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
                    <MapPin className="w-3 h-3" /> {selectedEntry.candidate_location}
                  </p>
                )}

                {/* Stage selector */}
                <div className="mb-5">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 block">Stage</label>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(STAGE_CONFIG).map(([stage, cfg]) => (
                      <button
                        key={stage}
                        onClick={() => moveStage(selectedEntry.entry_id, stage)}
                        className={`text-xs px-2.5 py-1 border transition-colors ${
                          selectedEntry.stage === stage
                            ? `${cfg.color} ${cfg.bg} ${cfg.text}`
                            : "border-[#222] text-gray-500 hover:border-[#333]"
                        }`}
                        data-testid={`move-to-${stage}`}
                      >
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div className="mb-5">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 block">Tags</label>
                  <div className="flex flex-wrap gap-1.5">
                    {TAG_PRESETS.map(tag => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(selectedEntry.entry_id, selectedEntry.tags || [], tag)}
                        className={`text-xs px-2 py-0.5 border transition-colors ${
                          selectedEntry.tags?.includes(tag)
                            ? "border-crimson bg-crimson/10 text-crimson"
                            : "border-[#222] text-gray-500 hover:border-[#333]"
                        }`}
                        data-testid={`tag-${tag.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        <Tag className="w-2.5 h-2.5 inline mr-1" />{tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mb-5">
                  <Button size="sm" onClick={() => markContacted(selectedEntry.entry_id)}
                    className="bg-blue-500/20 border border-blue-500/30 text-blue-400 hover:bg-blue-500/30 text-xs" data-testid="mark-contacted-btn">
                    <Check className="w-3 h-3 mr-1" /> Mark Contacted
                  </Button>
                  <Button size="sm" onClick={() => setReminder(selectedEntry.entry_id, 3)}
                    className="bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 text-xs" data-testid="set-reminder-btn">
                    <Bell className="w-3 h-3 mr-1" /> Remind in 3d
                  </Button>
                  <Button size="sm" onClick={() => removeEntry(selectedEntry.entry_id)}
                    className="bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-xs" data-testid="remove-entry-btn">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>

                {/* Skills */}
                {selectedEntry.candidate_skills?.length > 0 && (
                  <div className="mb-5">
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 block">Skills</label>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedEntry.candidate_skills.map(skill => (
                        <span key={skill} className="text-[10px] px-2 py-0.5 bg-[#111] border border-[#222] text-gray-400">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 block">Notes ({selectedEntry.notes?.length || 0})</label>
                  <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
                    {(selectedEntry.notes || []).map((note) => (
                      <div key={note.note_id} className="p-2.5 bg-[#111] border border-[#1a1a1a] text-sm">
                        <p className="text-gray-300">{note.text}</p>
                        <p className="text-[9px] text-gray-600 mt-1">
                          {note.author} — {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                    {(!selectedEntry.notes || selectedEntry.notes.length === 0) && (
                      <p className="text-xs text-gray-600 py-3 text-center">No notes yet</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note..."
                      className="flex-1 bg-[#111] border border-[#222] px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-crimson focus:outline-none"
                      onKeyDown={(e) => e.key === "Enter" && addNote()}
                      data-testid="add-note-input"
                    />
                    <Button size="sm" onClick={addNote} disabled={addingNote || !newNote.trim()}
                      className="bg-crimson hover:bg-crimson/90 text-white" data-testid="add-note-btn">
                      <Send className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RecruiterCRM;
